import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';

import { autorun, runInAction } from 'mobx';
import bbox from '@turf/bbox';
import { featureCollection, lineString, point, combine } from '@vromo/geojson';
import { useStore } from '@vromo/mobx-react';
import { useLocalObservable } from 'mobx-react-lite';
import { observer } from 'mobx-react-lite';
import mapboxgl from 'mapbox-gl';
import some from 'lodash/some';
import Tracking from '@vromo/analytics';
import { css } from 'emotion';
import ReactDOM from 'react-dom';

import useStylesheets from '@vromo/hooks/useStylesheets';
import FontAwesome from '@vromo/ui/components/FontAwesome';

import { RootStore } from '../stores/RootStore';

import MapboxStyle from 'mapbox-gl/dist/mapbox-gl.css';
import MapboxTrafficStyle from '@mapbox/mapbox-gl-traffic/mapbox-gl-traffic.css';
import { mapboxStyles, secrets } from '@vromo/api';
import { isAllowedByOptions } from '@vromo/utils/server';

const TrackButton = observer(function TrackButton({ state }) {
    if (!state.tracking) {
        return (
            <button
                style={{
                    padding: 6,
                    borderRadius: 4,
                    lineHeight: 1,
                    backgroundColor: '#fff',
                    boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
                    color: 'black',
                    cursor: 'pointer'
                }}
                onClick={() => {
                    runInAction(() => {
                        state.tracking = true;
                    });
                }}>
                <FontAwesome icon="crosshairs" style={{ fontSize: 18 }} />
            </button>
        );
    } else {
        return null;
    }
});

class TrackControl {
    state: any = null;
    container: any = null;

    constructor(state) {
        this.state = state;
    }

    onAdd(map) {
        this.container = document.createElement('div');
        this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

        ReactDOM.render(<TrackButton state={this.state} />, this.container);

        return this.container;
    }

    onRemove(map) {
        ReactDOM.unmountComponentAtNode(this.container);
    }
}

function loadImage(map, url, name) {
    map.loadImage(url, (err, image) => {
        if (map) map.addImage(name, image);
    });
}

const Container = styled.div`
    flex: 1;
    background-color: white;
    position: relative;
    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
`;

const DEFAULT_SOURCE = {
    type: 'geojson',
    data: {
        type: 'FeatureCollection',
        features: []
    }
};

const supported = isAllowedByOptions() && mapboxgl.supported();

export default observer(function PlanMap({ width, height, selected, store }: { selected: Set }) {
    useStylesheets(MapboxStyle, MapboxTrafficStyle);

    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const state = useLocalObservable(() => ({ tracking: true }));

    const rootStore = useStore<RootStore>('rootStore');

    const { jobStore, vehicleStore, groupStore } = rootStore;

    function fitBounds(linear = false, userDidntInteract = false) {
        const map = mapRef.current;
        const bounds = bbox(
            combine(
                featureCollection(
                    map
                        .getSource('jobs')
                        ._data.features.filter((it) =>
                            selected.size ? selected.has(it.properties.id) : true
                        )
                ),
                featureCollection(
                    map
                        .getSource('vehicles')
                        ._data.features.filter((it) =>
                            selected.size ? selected.has(it.properties.id) : true
                        )
                )
            )
        );

        if (!some(bounds, (it) => it === Infinity)) {
            map.fitBounds(
                bounds,
                {
                    padding: {
                        top: 40,
                        bottom: 40,
                        left: 40,
                        right: 40
                    },
                    linear: false,
                    maxZoom: 16
                },
                {
                    userDidntInteract
                }
            );
        }
    }

    useEffect(() => {
        if (mapRef.current) mapRef.current.resize();
    }, [width, height]);

    useEffect(() => {
        if (!supported) {
            Tracking.track('MapboxGL Not Supported');
            return;
        }

        const subscriptions = [];

        mapboxgl.accessToken = secrets.mapbox;


        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapboxStyles.light,
            refreshExpiredTiles: false,
            zoom: 1,
            center: [1, 1],
            boxZoom: false,
            dragRotate: false
        });

        map.addControl(new TrackControl(state), 'bottom-left');

        mapRef.current = map;

        map.on('load', () => {
            map.on('mouseenter', 'jobs-layer', () => (map.getCanvas().style.cursor = 'pointer'));
            map.on('mouseleave', 'jobs-layer', () => (map.getCanvas().style.cursor = ''));
            map.on(
                'mouseenter',
                'vehicles-layer',
                () => (map.getCanvas().style.cursor = 'pointer')
            );
            map.on('mouseleave', 'vehicles-layer', () => (map.getCanvas().style.cursor = ''));

            map.on('click', (e) => {
                const renderedFeatures = map.queryRenderedFeatures(e.point, {
                    layers: ['jobs-layer', 'vehicles-layer']
                });

                if (renderedFeatures.length) {
                    const feature = renderedFeatures[0];

                    if (!(e.originalEvent.metaKey || e.originalEvent.ctrlKey)) {
                        selected.clear();
                    }

                    switch (feature.layer.id) {
                        case 'vehicles-layer':
                            store.vehicle = feature.properties.id;
                            break;
                        case 'jobs-layer':
                            if (feature.properties.status === 'open') {
                                selected.add(feature.properties.id);
                            } else {
                                store.vehicle = feature.properties.vehicle;
                            }
                            break;
                    }
                } else {
                    store.vehicle = null;
                    selected.clear();
                }
            });

            map.on('move', (e) => {
                if (!e.userDidntInteract && state.tracking) {
                    runInAction(() => (state.tracking = false));
                }
            });

            map.addSource('routes', DEFAULT_SOURCE);
            map.addSource('jobs', DEFAULT_SOURCE);
            map.addSource('vehicles', DEFAULT_SOURCE);

            map.addLayer({
                id: 'routes-layer',
                type: 'line',
                source: 'routes',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 6,
                    'line-opacity': 0.8
                }
            });

            map.addLayer({
                id: 'jobs-layer',
                type: 'symbol',
                source: 'jobs',
                layout: {
                    'icon-image': '{icon}',
                    'icon-size': 0.6,
                    'icon-allow-overlap': true,
                    'text-field': '{eta}',
                    'text-anchor': 'left',
                    'text-offset': [1.3, 0],
                    'text-size': 14,
                    'text-optional': true
                },

                paint: {
                    'text-color': '#000',
                    'icon-opacity': ['case', ['==', ['get', 'status'], 'open'], 1, 0.5]
                }

                // FIXME: lmao whats the bet that I have to uncomment it because deliverthat are retarded
                // filter: [
                //     'step',
                //     ['zoom'],
                //     ['any', ['get', 'vehicleSelected'], ['==', ['get', 'status'], 'open']],
                //     13,
                //     true
                // ]
            });

            map.addLayer({
                id: 'vehicles-layer',
                type: 'symbol',
                source: 'vehicles',
                layout: {
                    'icon-image': '{icon}',
                    'icon-size': 0.45,
                    'icon-rotate': ['number', ['get', 'heading'], 0],
                    'icon-allow-overlap': true,
                    'text-field': '{name}',
                    'text-anchor': 'left',
                    'text-offset': [1.8, 0],
                    'text-size': 12,
                    'text-optional': true
                },
                paint: {
                    'text-color': '#000'
                }
            });

            loadImage(map, require('@vromo/icons/pickup/dispatched/icon.png'), 'pickup');
            loadImage(map, require('@vromo/icons/pickup/icon.png'), 'undispatched');
            loadImage(map, require('@vromo/icons/dropoff/icon.png'), 'dropoff');
            loadImage(map, require('@vromo/icons/job/icon.png'), 'job');
            loadImage(map, require('@vromo/icons/car/icon.png'), 'vehicle');
            loadImage(map, require('@vromo/icons/pickup/highlighted.png'), 'pickup/highlighted');
            loadImage(
                map,
                require('@vromo/icons/pickup/highlighted.png'),
                'undispatched/highlighted'
            );
            loadImage(map, require('@vromo/icons/dropoff/highlighted.png'), 'dropoff/highlighted');
            loadImage(map, require('@vromo/icons/job/highlighted.png'), 'job/highlighted');
            loadImage(map, require('@vromo/icons/car/highlighted.png'), 'vehicle/highlighted');
            loadImage(map, require('@vromo/icons/car/no-jobs/icon.png'), 'vehicle/no-jobs');
            loadImage(
                map,
                require('@vromo/icons/car/no-jobs/highlighted.png'),
                'vehicle/no-jobs/highlighted'
            );

            subscriptions.push(
                autorun(
                    () => {
                        const jobs = store.jobs; // rootStore.filteredJobs;

                        map.getSource('routes').setData(
                            combine(
                                ...jobs
                                    .filter((it) => selected.has(it.id) || selected.has(it.vehicle))
                                    .map((job) =>
                                        featureCollection(
                                            job.route.slice(job.progress).map((route, ix) => {
                                                const vehicle =
                                                    job.vehicle &&
                                                    rootStore.vehicleStore.withId(job.vehicle);

                                                return lineString(
                                                    ix === 0 && vehicle && vehicle.location
                                                        ? [
                                                            [
                                                                vehicle.location.lon,
                                                                vehicle.location.lat
                                                            ],
                                                            ...(route.polyline
                                                                ? route.polyline
                                                                    .truncate([
                                                                        vehicle.location.lat,
                                                                        vehicle.location.lon
                                                                    ])
                                                                    .map(({ lon, lat }) => [
                                                                        lon,
                                                                        lat
                                                                    ])
                                                                : [])
                                                        ]
                                                        : route.polyline
                                                            ? route.polyline.decoded.map(
                                                                ({ lon, lat }) => [lon, lat]
                                                            )
                                                            : [],
                                                    {
                                                        color: selected.has(job.id)
                                                            ? '#29A6F2'
                                                            : '#F8B931',
                                                        dashed: ix === 0
                                                    }
                                                );
                                            })
                                        )
                                    )
                            )
                        );
                    },
                    { delay: 30 }
                )
            );

            subscriptions.push(
                autorun(
                    () => {
                        const vehicles = vehicleStore.filteredVehicles;

                        map.getSource('vehicles').setData(
                            featureCollection(
                                vehicles
                                    .filter((it) => it.location)
                                    .map((it) =>
                                        point(
                                            [
                                                it.location && it.location.lon,
                                                it.location && it.location.lat
                                            ],
                                            {
                                                id: it.id,
                                                icon:
                                                    (jobStore.withVehicle(it.id).length
                                                        ? 'vehicle'
                                                        : 'vehicle/no-jobs') +
                                                    (store.vehicle === it.id ? '/highlighted' : ''),
                                                heading: it.point.heading,
                                                name: it.displayName(
                                                    vehicleStore.driverVehicleMap,
                                                    groupStore.withId(it.group!)
                                                )
                                            }
                                        )
                                    )
                            )
                        );

                        if (state.tracking) fitBounds(true, true);
                    },
                    { delay: 30 }
                )
            );

            subscriptions.push(
                autorun(
                    () => {
                        const jobs = store.today; // rootStore.filteredJobs;

                        map.getSource('jobs').setData(
                            combine(
                                ...jobs.map((job) =>
                                    featureCollection(
                                        job.tasks.slice(job.progress).map((task) => {
                                            const pickupIx = job.tasks.length > 1 ? 0 : -1;
                                            const dropoffIx = job.tasks.length - 1;
                                            const ix = job.tasks.items.indexOf(task);

                                            return point(
                                                [task.zone.coords.lon, task.zone.coords.lat],
                                                {
                                                    id: job.id,
                                                    vehicle: job.vehicle,
                                                    status: job.status,
                                                    vehicleSelected:
                                                        !!job.vehicle &&
                                                        job.vehicle === store.vehicle,
                                                    icon:
                                                        (ix === pickupIx
                                                            ? job.status === 'dispatched' ||
                                                                job.status === 'underway'
                                                                ? 'pickup'
                                                                : 'undispatched'
                                                            : ix === dropoffIx
                                                                ? 'dropoff'
                                                                : 'job') +
                                                        (store.selected.has(job.id) ||
                                                            (job.vehicle &&
                                                                store.vehicle === job.vehicle)
                                                            ? '/highlighted'
                                                            : ''),
                                                    eta:
                                                        store.selected.has(job.id) &&
                                                            job.status === 'underway' &&
                                                            job.route.items.length > ix &&
                                                            job.route.items[ix] &&
                                                            job.route.items[ix].duration !==
                                                            undefined &&
                                                            job.route.items[ix].duration !== null
                                                            ? `+${Math.round(
                                                                job.route.items[ix].duration /
                                                                1000 /
                                                                60
                                                            )} mins`
                                                            : undefined
                                                }
                                            );
                                        })
                                    )
                                )
                            )
                        );

                        if (state.tracking) fitBounds(true, true);
                    },
                    { delay: 30 }
                )
            );
        });

        return () => {
            subscriptions.forEach((it) => it());
            map.remove();
        };
    }, []);

    if (!supported) {
        return (
            <Container
                width={width}
                height={height}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#eee',
                    userSelect: 'none'
                }}>
                <div
                    className={css`
                        text-align: center;
                        padding: 30px;
                    `}>
                    Something went wrong while trying to draw the map, check that your browser is up
                    to date and refresh the page
                </div>
            </Container>
        );
    }

    return (
        <Container width={width} height={height}>
            <div ref={mapContainerRef} style={{ width, height }} />
        </Container>
    );
});
